import React from 'react'
import PropTypes from 'prop-types'
import Skeleton from '@material-ui/lab/Skeleton'
import { ThemeProvider, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import themes, { extendTheme } from 'themes'
import SchedulePageBackground from '../../components/page-background'
import useStyles, { PAGE_BACKGROUND } from './styles'

const refreshTheme = extendTheme(themes.offeringsRefresh)

// Placeholder pieces shared by the loading states of all three redesigned
// private booking steps. Each mirrors a piece of the loaded page (same class
// names from the step 1 redesign sheet) so the page does not shift when the
// real content arrives.

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

export const SkeletonBlock = ({ width, height, radius = 8, style }) => (
  <Skeleton
    variant="rect"
    animation="wave"
    width={width}
    height={height}
    style={{ borderRadius: radius, ...style }}
  />
)

SkeletonBlock.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  radius: PropTypes.number,
  style: PropTypes.object,
}

export const SkeletonText = ({ width, height, style }) => (
  <Skeleton
    variant="text"
    animation="wave"
    width={width}
    height={height}
    style={style}
  />
)

SkeletonText.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.object,
}

// The back link row on desktop, the compact top card (back link + coach and
// lesson summary) below the `md` breakpoint.
export const TopSkeleton = ({ isCompact }) => {
  const classes = useStyles()
  if (isCompact) {
    return (
      <div className={classes.mobileTopCard}>
        <div className={classes.mobileBackRow}>
          <div style={{ padding: '16px 4px' }}>
            <SkeletonText width={64} height={20} />
          </div>
        </div>
        <div className={classes.mobileSummary}>
          <SkeletonBlock width={40} height={40} radius={20} />
          <div className={classes.mobileSummaryText}>
            <SkeletonText width="70%" height={20} />
            <SkeletonText width="45%" height={18} />
          </div>
          <SkeletonText width={44} height={20} />
        </div>
      </div>
    )
  }
  return (
    <div className={classes.desktopBack}>
      <div
        style={{
          marginTop: 16,
          height: 48,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <SkeletonText width={72} height={24} />
      </div>
    </div>
  )
}

TopSkeleton.propTypes = { isCompact: PropTypes.bool.isRequired }

// Sticky summary card (coach row, lesson title, facts, total).
export const SummaryCardSkeleton = () => {
  const classes = useStyles()
  return (
    <div className={classes.summaryCard} data-testid="summary-card-skeleton">
      <div className={classes.coachRow}>
        <SkeletonBlock width={48} height={48} radius={24} />
        <div style={{ flex: 1 }}>
          <SkeletonText width="60%" height={24} />
          <SkeletonBlock width={96} height={24} radius={12} />
        </div>
      </div>
      <hr className={classes.summaryDivider} />
      <SkeletonText width="75%" height={28} style={{ marginBottom: 16 }} />
      <ul className={classes.facts}>
        {[85, 60, 40, 70].map((width, index) => (
          <li className={classes.fact} key={index}>
            <SkeletonBlock width={18} height={18} radius={9} />
            <SkeletonText width={`${width}%`} height={20} />
          </li>
        ))}
      </ul>
      <hr className={classes.summaryDivider} />
      <div className={classes.totalRow}>
        <SkeletonText width={48} height={24} />
        <SkeletonText width={72} height={36} />
      </div>
    </div>
  )
}

// The "Your time" strip at the top of steps 2 and 3.
// `className` is the step's own "Your time" card class (steps 2 and 3 each
// define it in their sheet).
export const YourTimeSkeleton = ({ className }) => (
  <div className={className} data-testid="your-time-skeleton">
    <div style={{ flex: 1 }}>
      <SkeletonText width={72} height={16} />
      <SkeletonText width="50%" height={24} />
    </div>
    <SkeletonBlock width={64} height={32} radius={16} />
  </div>
)

YourTimeSkeleton.propTypes = { className: PropTypes.string.isRequired }

// Page chrome for a loading redesigned step: grey page background, same
// container, grid and gutters as the loaded page, the top row, the main column
// and (on desktop) the 340px summary column. Announced once to screen readers
// instead of every placeholder.
export const SchedulePageSkeletonFrame = ({ testId, children }) => {
  const classes = useStyles()
  const theme = useTheme()
  // Same breakpoint the loaded redesign uses for its compact layout.
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <ThemeProvider theme={refreshTheme}>
      <SchedulePageBackground color={PAGE_BACKGROUND} className={classes.page}>
        <div
          className={classes.inner}
          role="status"
          aria-busy="true"
          data-testid={testId}
        >
          <span style={visuallyHidden}>Loading</span>
          <TopSkeleton isCompact={isCompact} />
          <div className={classes.grid}>
            <div className={classes.mainCol}>{children({ isCompact })}</div>
            {!isCompact && (
              <div className={classes.sideCol}>
                <SummaryCardSkeleton />
              </div>
            )}
          </div>
        </div>
      </SchedulePageBackground>
    </ThemeProvider>
  )
}

SchedulePageSkeletonFrame.propTypes = {
  testId: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
}
